import React, { useCallback } from "react";
import { HStack } from "web/react/emo/h-stack";
import { Link } from "web/react/emo/link";
import { Text } from "web/react/emo/text";
import { View } from "web/react/emo/view";
import {
    EditorialArticleCardSchemaProps,
    useArticleAnalytics,
} from "web/react/hooks/use-article-analytics/use-article-analytics";
import { gettext } from "web/script/modules/django-i18n";
import { ArticleCardSerializer } from "web/types/serializers";
import { InFeedArticleCard } from "./in-feed-article-card";
import * as styles from "./in-feed-editorial-block.css";

interface InFeedEditorialBlockProps {
    articles: ArticleCardSerializer[];
}

export function InFeedEditorialBlock({ articles }: InFeedEditorialBlockProps): React.ReactElement {
    const primaryArticle = articles[0];
    const secondaryArticles = articles.length > 1 ? articles.slice(1, 4) : null;

    const bodyDesktopTopMargin = articles.length > 1 ? "sm" : "md";
    const bodyDesktopBottomMargin = articles.length > 1 ? "xs" : "md";

    const createAnalyticsItemData = useCallback(
        (
            article: ArticleCardSerializer,
            componentType: string
        ): EditorialArticleCardSchemaProps => ({
            item_type: "editorial_content_card",
            component_type: componentType,
            editorial_label: article.slug,
            franchise: article.franchise || null,
            editorial_gender: article.gender,
            position: article.position,
            sponsored: article.is_sponsored,
            content_type: article.content_type || "editorial",
            is_pinned: article.is_pinned,
            parent_area: "feeds",
        }),
        []
    );

    const generateArticleCardAnalyticsProps = useCallback(() => {
        const articleCardProps: EditorialArticleCardSchemaProps[] = [];

        if (primaryArticle) {
            articleCardProps.push(
                createAnalyticsItemData(primaryArticle, "editorial_primary_card")
            );
        }

        if (secondaryArticles) {
            secondaryArticles.forEach((article) => {
                articleCardProps.push(createAnalyticsItemData(article, "editorial_secondary_card"));
            });
        }

        return articleCardProps;
    }, [primaryArticle, secondaryArticles, createAnalyticsItemData]);

    const { sendClickEvent, containerRef } = useArticleAnalytics({
        category: "editorial_content_block",
        itemData: generateArticleCardAnalyticsProps(),
    });

    return (
        <div ref={containerRef}>
            <View
                height="full"
                dataTestId="editorial-block"
                className={styles.editorialBlockContainer}
            >
                <View
                    marginTop={{ sm: "xs", md: "xs", lg: bodyDesktopTopMargin }}
                    marginBottom={{ sm: "xs", md: "xs", lg: bodyDesktopBottomMargin }}
                >
                    <View
                        marginBottom="sm"
                        onClick={() => sendClickEvent("cta", "view_all_button")}
                    >
                        <HStack justify="space-between" align="center">
                            <Text textStyle="body">{gettext("feed.editorial.recommended")}</Text>
                            <Link
                                title={gettext("site_links.view_all")}
                                href="/the-edit"
                                color="secondary"
                                dataTestId="editorial-block-view-all"
                            />
                        </HStack>
                    </View>
                    <View marginBottom="sm">
                        <InFeedArticleCard
                            variant="primary"
                            articleProps={primaryArticle}
                            analyticsComponentType={"editorial_primary_card"}
                        />
                    </View>
                    {secondaryArticles && (
                        <View className={styles.secondaryArticlesSectionContainer}>
                            {secondaryArticles.map((article) => (
                                <View
                                    key={article.slug}
                                    className={styles.secondaryArticleContainer}
                                >
                                    <InFeedArticleCard
                                        variant="secondary"
                                        articleProps={article}
                                        analyticsComponentType={"editorial_secondary_card"}
                                    />
                                </View>
                            ))}
                        </View>
                    )}
                </View>
            </View>
        </div>
    );
}
